import { ROUND_TO_INT_CURRENCIES } from "@/utils/Const";

/**
 * Provides optimistic quantity update logic for cart components.
 * Requires the component to have:
 * - `this.order` (computed from Vuex cart.cartData)
 * - `this.$store.commit('setCartData', data)`
 */
export default {
  methods: {
    optimisticQuantityUpdate({ orderLineId, quantity }) {
      const order = this.order;
      if (!order) return;
      const updated = JSON.parse(JSON.stringify(order));
      const edge = updated.orderLines.edges.find((e) => e.node.id === orderLineId);
      if (!edge) return;
      const line = edge.node;
      const oldQty = line.quantity;
      if (quantity === oldQty) return;

      // Line fields (price, basePrice, discount) are LINE TOTALS (already include quantity).
      // preTaxPrice is per-unit and stays the same.
      // Derive per-unit values from old totals, then multiply by new quantity
      // to avoid floating-point drift from repeated ratio multiplication.
      const currencyCode = updated.currency?._id || '';
      const isIntCurrency = ROUND_TO_INT_CURRENCIES.includes(currencyCode);
      const precision = isIntCurrency ? 0 : 2;
      const factor = Math.pow(10, precision);
      const round = (v) => Math.round(v * factor) / factor;

      const unitPrice = Number(line.price) / oldQty;
      const unitBasePrice = Number(line.basePrice) / oldQty;
      const unitDiscount = Number(line.discount || 0) / oldQty;
      line.price = round(unitPrice * quantity);
      line.basePrice = round(unitBasePrice * quantity);
      line.discount = round(unitDiscount * quantity);
      line.preTaxPrice = Number(line.preTaxPrice);  // per-unit, stays same
      line.quantity = quantity;

      // Recalculate order totals to match backend logic:
      // - line.price is POST-TAX (final price including tax)
      // - totalLineItemsPrice = sum of (preTaxPrice * quantity) per line (pre-tax)
      // - subtotalPrice = totalLineItemsPrice - totalDiscount + shippingFee
      // - totalPrice = sum of line.price (post-tax) + shippingFee (no tax on shipping)
      let totalPostTax = 0;
      let totalDiscount = 0;
      let totalLineItems = 0;
      for (const e of updated.orderLines.edges) {
        const l = e.node;
        totalPostTax += Number(l.price);
        totalDiscount += Number(l.discount || 0);
        totalLineItems += round(Number(l.preTaxPrice) * l.quantity);
      }
      const shippingFee = Number(updated.shippingFee || 0);
      const subtotal = totalLineItems - totalDiscount + shippingFee;
      updated.totalLineItemsPrice = round(totalLineItems);
      updated.totalDiscount = round(totalDiscount);
      updated.subtotalPrice = round(subtotal);
      updated.totalTax = round(totalPostTax + shippingFee - subtotal);
      updated.totalPrice = round(totalPostTax + shippingFee);
      if (this.isBuyNowFlow) {
        this.buyNowOrderData = updated;
      } else {
        this.$store.commit("setCartData", updated);
      }
    },
  },
};
